import { c as _c } from "react/compiler-runtime";
import { FlagSolid, HomeSolid, MapPinSolid, PinSolid } from '@byway/icons';
import clsx from 'clsx';
import { usePathname } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { useInView } from 'react-intersection-observer';
import { LoadingSpinner } from '~/components/LoadingSpinner/LoadingSpinner';
import { getMapOverview, type MapOverviewItem, safeFormatDate } from '~/components/Playground/mapHelpers';
import { StadiaMap } from '~/components/StadiaMap';
import useJourneyData from '~/hooks/useJourneyData';
import type { TimelineItem } from '~/transformers/Dato/TimelineTransformer';
import type { AnyJourneyItemTransformed, StayTransformed } from '~/types/JourneyItem.types';
import { Heading } from '../../Heading/src/Heading';
import { EditInMapsButton } from '../EditInMapsButton/EditInMapsButton';
import styles from './JourneyMap.module.css';
import type { JourneyMapForJourneyGuideProps, JourneyMapProps } from './JourneyMap.types';
import { OverviewNightsAdjuster } from './OverviewNightsAdjuster';
export function JourneyMap(t0) {
  const $ = _c(29);
  const {
    items,
    journeyType,
    canEditNights,
    journeyId
  } = t0;
  const tJourney = useTranslations("journey");
  const t = useTranslations("content.feedback");
  const isTemplate = journeyType === "asTemplate";
  const isJourneyGuide = journeyType === "journeyGuide";
  let t1;
  if ($[0] !== items) {
    t1 = items.filter(_temp);
    $[0] = items;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const legs = t1;
  const overview = getMapOverview(items);
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      triggerOnce: true,
      threshold: 0.1,
      rootMargin: "50px"
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const {
    ref: mapRef,
    inView
  } = useInView(t2);
  let t3;
  if ($[3] !== isJourneyGuide) {
    t3 = clsx(styles.container, {
      [styles.jgSpacing]: isJourneyGuide
    });
    $[3] = isJourneyGuide;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== isJourneyGuide || $[6] !== tJourney) {
    t4 = !isJourneyGuide && <div className={styles.heading}><Heading level="h3" variant="heading-sm">{tJourney("your_route")}</Heading></div>;
    $[5] = isJourneyGuide;
    $[6] = tJourney;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== isJourneyGuide) {
    t5 = clsx(styles.content, {
      [styles.forceMobile]: isJourneyGuide,
      [styles.reverseOrder]: isJourneyGuide
    });
    $[8] = isJourneyGuide;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const t6 = styles;
  const t7 = inView ? <StadiaMap mapOverview={overview.filter(_temp2)} routes={legs} maxZoom={16} /> : <div className={styles.mapLoading}><LoadingSpinner title={t("map_loading")} /></div>;
  let t8;
  if ($[10] !== mapRef || $[11] !== t6.mapContainer || $[12] !== t7) {
    t8 = <div ref={mapRef} className={t6.mapContainer}>{t7}</div>;
    $[10] = mapRef;
    $[11] = t6.mapContainer;
    $[12] = t7;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== canEditNights || $[15] !== isJourneyGuide || $[16] !== isTemplate || $[17] !== items || $[18] !== journeyId || $[19] !== overview) {
    t9 = <TripOverview overview={overview} isJourneyGuide={isJourneyGuide} isTemplate={isTemplate} items={items} canEditNights={canEditNights} journeyId={journeyId} />;
    $[14] = canEditNights;
    $[15] = isJourneyGuide;
    $[16] = isTemplate;
    $[17] = items;
    $[18] = journeyId;
    $[19] = overview;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== t5 || $[22] !== t8 || $[23] !== t9) {
    t10 = <div className={t5}>{t8}{t9}</div>;
    $[21] = t5;
    $[22] = t8;
    $[23] = t9;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  let t11;
  if ($[25] !== t10 || $[26] !== t3 || $[27] !== t4) {
    t11 = <div className={t3}>{t4}{t10}</div>;
    $[25] = t10;
    $[26] = t3;
    $[27] = t4;
    $[28] = t11;
  } else {
    t11 = $[28];
  }
  return t11;
}
function _temp2(item_0) {
  return !(item_0.type === "Journey" && item_0.timelinePosition === "middle");
}
function _temp(item) {
  return item.type === "Journey";
}
type TripOverviewProps = {
  overview: MapOverviewItem[];
  isJourneyGuide?: boolean;
  isTemplate: boolean;
  isInteractive?: boolean;
  items?: AnyJourneyItemTransformed[];
  canEditNights?: boolean;
  journeyId?: string;
};
type TimelineIconProps = {
  isOrigin: boolean;
  isDestination: boolean;
  isRoundTrip: boolean;
  isEssence: boolean;
  isStop: boolean;
};
function TimelineIcon(t0) {
  const $ = _c(14);
  const {
    isOrigin,
    isDestination,
    isRoundTrip,
    isEssence,
    isStop
  } = t0;
  if (isOrigin) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <div className={clsx(styles.timelineIconCircle, styles.originCircle)}><HomeSolid color="var(--by-color-icon-contrast)" size="small" decorative={true} /></div>;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  if (isDestination) {
    let t1;
    if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = clsx(styles.timelineIconCircle, styles.endCircle);
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    let t2;
    if ($[2] !== isRoundTrip) {
      t2 = <div className={t1}>{isRoundTrip ? <HomeSolid color="var(--by-color-icon-contrast)" size="small" decorative={true} /> : <FlagSolid color="var(--by-color-icon-contrast)" size="small" decorative={true} />}</div>;
      $[2] = isRoundTrip;
      $[3] = t2;
    } else {
      t2 = $[3];
    }
    return t2;
  }
  const t1 = !isEssence && !isStop;
  let t2;
  if ($[4] !== isEssence || $[5] !== isStop || $[6] !== t1) {
    t2 = clsx(styles.timelineIconCircle, {
      [styles.stayCircle]: isEssence,
      [styles.nonEssenceCircle]: t1,
      [styles.stopCircle]: isStop
    });
    $[4] = isEssence;
    $[5] = isStop;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== isEssence || $[9] !== isStop) {
    t3 = !isStop && (isEssence ? <PinSolid color="var(--by-color-icon-contrast)" size="small" decorative={true} /> : <MapPinSolid color="var(--by-color-icon-contrast)" size="small" decorative={true} />);
    $[8] = isEssence;
    $[9] = isStop;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  let t4;
  if ($[11] !== t2 || $[12] !== t3) {
    t4 = <div className={t2}>{t3}</div>;
    $[11] = t2;
    $[12] = t3;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  return t4;
}
type ItemDetailsProps = {
  item: MapOverviewItem;
  isOrigin: boolean;
  isStop: boolean;
  isTemplate: boolean;
  stayItem?: StayTransformed;
  journeyId?: string;
};
function ItemDetails(t0) {
  const $ = _c(27);
  const {
    item,
    isOrigin,
    isStop,
    isTemplate,
    stayItem,
    journeyId
  } = t0;
  const t = useTranslations("journey");
  if (item.type === "Stay" || item.isSleeper || item.type === "Journey" && item.timelinePosition === "middle") {
    let t1;
    if ($[0] !== journeyId || $[1] !== stayItem) {
      t1 = stayItem && journeyId && <div className={styles.overviewAdjusterWrapper}><OverviewNightsAdjuster item={stayItem} journeyId={journeyId} /></div>;
      $[0] = journeyId;
      $[1] = stayItem;
      $[2] = t1;
    } else {
      t1 = $[2];
    }
    let t2;
    if ($[3] !== item.nights || $[4] !== t) {
      t2 = item.nights && item.nights > 0 ? t("nights_count", {
        count: item.nights
      }) : t("map_stop");
      $[3] = item.nights;
      $[4] = t;
      $[5] = t2;
    } else {
      t2 = $[5];
    }
    let t3;
    if ($[6] !== t2) {
      t3 = <span>{t2}</span>;
      $[6] = t2;
      $[7] = t3;
    } else {
      t3 = $[7];
    }
    let t4;
    if ($[8] !== isTemplate || $[9] !== item.endDate || $[10] !== item.startDate) {
      t4 = !isTemplate && <span className={styles.dateRange}>{safeFormatDate(item.startDate, item.endDate)}</span>;
      $[8] = isTemplate;
      $[9] = item.endDate;
      $[10] = item.startDate;
      $[11] = t4;
    } else {
      t4 = $[11];
    }
    let t5;
    if ($[12] !== t3 || $[13] !== t4) {
      t5 = <p className={styles.itemDetails}>{t3}{t4}</p>;
      $[12] = t3;
      $[13] = t4;
      $[14] = t5;
    } else {
      t5 = $[14];
    }
    let t6;
    if ($[15] !== t1 || $[16] !== t5) {
      t6 = <>{t1}{t5}</>;
      $[15] = t1;
      $[16] = t5;
      $[17] = t6;
    } else {
      t6 = $[17];
    }
    return t6;
  }
  if (isStop) {
    let t1;
    if ($[18] !== t) {
      t1 = t("map_stop");
      $[18] = t;
      $[19] = t1;
    } else {
      t1 = $[19];
    }
    let t2;
    if ($[20] !== t1) {
      t2 = <span className={styles.positionText}>{t1}</span>;
      $[20] = t1;
      $[21] = t2;
    } else {
      t2 = $[21];
    }
    return t2;
  }
  let t1;
  if ($[22] !== isOrigin || $[23] !== t) {
    t1 = isOrigin ? t("map_start") : t("map_end");
    $[22] = isOrigin;
    $[23] = t;
    $[24] = t1;
  } else {
    t1 = $[24];
  }
  let t2;
  if ($[25] !== t1) {
    t2 = <span className={styles.positionText}>{t1}</span>;
    $[25] = t1;
    $[26] = t2;
  } else {
    t2 = $[26];
  }
  return t2;
}
type TripOverviewItemProps = {
  item: MapOverviewItem;
  isRoundTrip: boolean;
  isTripPage: boolean;
  isInteractive: boolean;
  isTemplate: boolean;
  items?: AnyJourneyItemTransformed[];
  canEditNights?: boolean;
  journeyId?: string;
};
function TripOverviewItem(t0) {
  const $ = _c(31);
  const {
    item,
    isRoundTrip,
    isTripPage,
    isInteractive,
    isTemplate,
    items,
    canEditNights,
    journeyId
  } = t0;
  const t = useTranslations("journey");
  const isOrigin = item.timelinePosition === "start";
  const isDestination = item.timelinePosition === "end";
  let t1;
  if ($[0] !== canEditNights || $[1] !== isTripPage || $[2] !== item.id || $[3] !== item.type || $[4] !== items || $[5] !== journeyId) {
    t1 = isTripPage && canEditNights && journeyId && item.type === "Stay" ? items?.find(s => s.id === item.id && s.type === "Stay") as StayTransformed | undefined : undefined;
    $[0] = canEditNights;
    $[1] = isTripPage;
    $[2] = item.id;
    $[3] = item.type;
    $[4] = items;
    $[5] = journeyId;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  const stayItem = t1;
  const isStop = item.type === "Stop";
  let t2;
  if ($[7] !== item.isSleeper || $[8] !== item.title || $[9] !== t) {
    t2 = item.isSleeper ? t("overview_sleeper_with_cabin") : item.title;
    $[7] = item.isSleeper;
    $[8] = item.title;
    $[9] = t;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  const title = t2;
  let t3;
  if ($[11] !== isDestination || $[12] !== isOrigin || $[13] !== isRoundTrip || $[14] !== isStop) {
    t3 = <TimelineIcon isOrigin={isOrigin} isDestination={isDestination} isRoundTrip={isRoundTrip} isEssence={false} isStop={isStop} />;
    $[11] = isDestination;
    $[12] = isOrigin;
    $[13] = isRoundTrip;
    $[14] = isStop;
    $[15] = t3;
  } else {
    t3 = $[15];
  }
  let t4;
  if ($[16] !== isInteractive || $[17] !== item.id || $[18] !== title) {
    t4 = isInteractive ? <a href={`#${item.id}`} className={clsx(styles.underline, styles.itemTitle)}>{title}</a> : <p className={styles.itemTitle}>{title}</p>;
    $[16] = isInteractive;
    $[17] = item.id;
    $[18] = title;
    $[19] = t4;
  } else {
    t4 = $[19];
  }
  let t5;
  if ($[20] !== isOrigin || $[21] !== isStop || $[22] !== isTemplate || $[23] !== item || $[24] !== journeyId || $[25] !== stayItem) {
    t5 = <ItemDetails item={item} isOrigin={isOrigin} isStop={isStop} isTemplate={isTemplate} stayItem={stayItem} journeyId={journeyId} />;
    $[20] = isOrigin;
    $[21] = isStop;
    $[22] = isTemplate;
    $[23] = item;
    $[24] = journeyId;
    $[25] = stayItem;
    $[26] = t5;
  } else {
    t5 = $[26];
  }
  let t6;
  if ($[27] !== t3 || $[28] !== t4 || $[29] !== t5) {
    t6 = <li className={styles.overviewItem}>{t3}{t4}{t5}</li>;
    $[27] = t3;
    $[28] = t4;
    $[29] = t5;
    $[30] = t6;
  } else {
    t6 = $[30];
  }
  return t6;
}
export function TripOverview(t0) {
  const $ = _c(37);
  const {
    overview,
    isJourneyGuide: t1,
    isTemplate,
    isInteractive: t2,
    items,
    canEditNights,
    journeyId
  } = t0;
  const isJourneyGuide = t1 === undefined ? false : t1;
  const isInteractive = t2 === undefined ? true : t2;
  const pathname = usePathname();
  const t = useTranslations("journey");
  let t3;
  if ($[0] !== pathname) {
    t3 = pathname?.includes("/journey/") || pathname?.includes("/journey-template/");
    $[0] = pathname;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const isTripPage = t3;
  const isRoundTrip = overview[0]?.title === overview[overview.length - 1]?.title;
  let t4;
  if ($[2] !== isJourneyGuide) {
    t4 = clsx(styles.overviewContainer, {
      [styles.forceMobile]: isJourneyGuide,
      [styles.bgWhite]: isJourneyGuide
    });
    $[2] = isJourneyGuide;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== t) {
    t5 = t("trip_overview");
    $[4] = t;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== t5) {
    t6 = <strong className={styles.overviewTitle}>{t5}</strong>;
    $[6] = t5;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== isTripPage) {
    t7 = isTripPage && <EditInMapsButton context="trip_overview" />;
    $[8] = isTripPage;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] !== t6 || $[11] !== t7) {
    t8 = <div className={styles.overviewHeader}>{t6}{t7}</div>;
    $[10] = t6;
    $[11] = t7;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <div aria-hidden="true" className={styles.timelineLine} />;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] !== canEditNights || $[15] !== isInteractive || $[16] !== isRoundTrip || $[17] !== isTemplate || $[18] !== isTripPage || $[19] !== items || $[20] !== journeyId || $[21] !== overview) {
    let t11;
    if ($[23] !== canEditNights || $[24] !== isInteractive || $[25] !== isRoundTrip || $[26] !== isTemplate || $[27] !== isTripPage || $[28] !== items || $[29] !== journeyId) {
      t11 = item => <TripOverviewItem key={`${item.id}-${item.timelinePosition}`} item={item} isRoundTrip={isRoundTrip} isTripPage={Boolean(isTripPage)} isInteractive={isInteractive} isTemplate={isTemplate} items={items} canEditNights={canEditNights} journeyId={journeyId} />;
      $[23] = canEditNights;
      $[24] = isInteractive;
      $[25] = isRoundTrip;
      $[26] = isTemplate;
      $[27] = isTripPage;
      $[28] = items;
      $[29] = journeyId;
      $[30] = t11;
    } else {
      t11 = $[30];
    }
    t10 = overview.map(t11);
    $[14] = canEditNights;
    $[15] = isInteractive;
    $[16] = isRoundTrip;
    $[17] = isTemplate;
    $[18] = isTripPage;
    $[19] = items;
    $[20] = journeyId;
    $[21] = overview;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[31] !== t10) {
    t11 = <div className={styles.overviewContent} data-testid="trip-overview-content">{t9}<ul className={styles.overviewList} data-testid="checkout-overview-list">{t10}</ul></div>;
    $[31] = t10;
    $[32] = t11;
  } else {
    t11 = $[32];
  }
  let t12;
  if ($[33] !== t11 || $[34] !== t4 || $[35] !== t8) {
    t12 = <div className={t4} data-testid="checkout-trip-overview">{t8}{t11}</div>;
    $[33] = t11;
    $[34] = t4;
    $[35] = t8;
    $[36] = t12;
  } else {
    t12 = $[36];
  }
  return t12;
}

// Wrapper for trip pages that use the journey store
export function JourneyMapForTrip() {
  const $ = _c(5);
  const {
    items,
    journeyType,
    allowItineraryEdits,
    overrideEditableTrip,
    urlPath,
    uniqueId
  } = useJourneyData();
  const isBlueprintUrl = urlPath === "journey-template";
  const canEditNights = !isBlueprintUrl && allowItineraryEdits && overrideEditableTrip !== false;
  const t0 = journeyType ?? "trip";
  let t1;
  if ($[0] !== canEditNights || $[1] !== items || $[2] !== t0 || $[3] !== uniqueId) {
    t1 = <JourneyMap items={items} journeyType={t0} canEditNights={canEditNights} journeyId={uniqueId} />;
    $[0] = canEditNights;
    $[1] = items;
    $[2] = t0;
    $[3] = uniqueId;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return t1;
}

// Wrapper for journey guide pages
export function JourneyMapForJourneyGuide(t0) {
  const $ = _c(4);
  const {
    timelineItems
  } = t0;
  const transformTimelineItems = _temp4;
  let t1;
  if ($[0] !== timelineItems) {
    t1 = transformTimelineItems(timelineItems);
    $[0] = timelineItems;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const items = t1;
  let t2;
  if ($[2] !== items) {
    t2 = <JourneyMap items={items} journeyType="journeyGuide" />;
    $[2] = items;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
}
function _temp4(timelineItems_0) {
  return timelineItems_0.map(_temp3);
}
function _temp3(item) {
  switch (item.type) {
    case "Journey":
      {
        return {
          id: item.id,
          type: "Journey" as const,
          itemId: parseInt(item.id, 10) || 0,
          duration: 0,
          nights: item.nights || 0,
          origin: item.origin,
          originId: 0,
          originGeoCoordinates: item.originGeoCoordinates || {
            latitude: 0,
            longitude: 0
          },
          fromTerminalId: "",
          fromTerminalName: null,
          fromTerminalGeoCoordinates: null,
          destination: item.destination,
          destinationId: 0,
          destinationGeoCoordinates: item.destinationGeoCoordinates || {
            latitude: 0,
            longitude: 0
          },
          toTerminalId: "",
          toTerminalName: null,
          toTerminalGeoCoordinates: null,
          departureTimeText: null,
          arrivalTimeText: null,
          description: `${item.origin} to ${item.destination}`,
          body: [],
          modeOfTransports: item.modeOfTransports ?? [],
          operator: null,
          transportClass: "STANDARD" as const,
          channel: null,
          interrail: false,
          startDate: item.startDate || "",
          endDate: item.endDate || "",
          carbonFootprint: -1,
          images: [],
          featured: false
        };
      }
    case "Stay":
      {
        return {
          id: item.id,
          type: "Stay" as const,
          stayItemId: parseInt(item.id, 10) || 0,
          title: item.title,
          country: {
            countryId: "",
            countryName: "",
            cmsId: ""
          },
          nights: item.nights || 0,
          geoCoordinates: item.geoCoordinates || {
            latitude: 0,
            longitude: 0
          },
          images: [],
          body: [],
          isEssencePlace: false,
          startDate: item.startDate || "",
          endDate: item.endDate || "",
          carbonFootprint: -1
        };
      }
    case "Stop":
      {
        return {
          id: item.id,
          type: "Stop" as const,
          title: item.title,
          nights: 0,
          geoCoordinates: item.geoCoordinates || {
            latitude: 0,
            longitude: 0
          },
          body: [],
          startDate: item.startDate || "",
          endDate: item.endDate || "",
          carbonFootprint: -1,
          images: []
        };
      }
    default:
      {
        throw new Error("Unknown timeline item type");
      }
  }
}